import { ReactNode } from 'react';
import { usePermissions } from 'hooks/usePermissions';
import type { AppPermission } from 'utils/permissions';

interface PermissionGateProps {
  /** Show if user has this single permission */
  permission?: AppPermission;
  /** Show if user has ANY of these permissions (OR logic, unless requireAll=true) */
  permissions?: AppPermission[];
  /** When true, requires ALL listed permissions instead of ANY */
  requireAll?: boolean;
  /** Show if user has this role (or any of the roles in the array) */
  role?: string | string[];
  /** Rendered when access is denied (defaults to null) */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Renders children only when the current user has the required permission/role.
 * Use for conditionally showing buttons, sections, or any UI element.
 *
 * For access enforcement on routes, use RouteGuard instead.
 */
export function PermissionGate({
  permission,
  permissions,
  requireAll = false,
  role,
  fallback = null,
  children,
}: PermissionGateProps) {
  const { can, canAny, canAll, hasRole } = usePermissions();

  let allowed = true;

  if (role !== undefined) {
    allowed = hasRole(role);
  }

  if (allowed && permission !== undefined) {
    allowed = can(permission);
  }

  if (allowed && permissions !== undefined && permissions.length > 0) {
    allowed = requireAll ? canAll(permissions) : canAny(permissions);
  }

  return allowed ? <>{children}</> : <>{fallback}</>;
}
